import React from 'react';
import {PostListItem} from '../../../../data/models/Posts/Post';
import Tooltip from '../../../../uiToolkit/Tooltip';
import ImageWithFallback from '../../../../uiToolkit/ImageWithFallback/ImageWithFallback';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import MicroreelCardOptions from '../../../Microreels/List/components/MicroreelCardOptions';
import {formatTimeAgo} from '../../../../utils/notificationTimeTextConvertor';
import {postsText} from '../../postsText';
import styles from './PostCard.module.css';

interface Props {
  post: PostListItem;
  onOpen: (post: PostListItem) => void;
  onEditInfo: (post: PostListItem) => void;
  onLearners: (post: PostListItem) => void;
  onLearnerProgress: (post: PostListItem) => void;
  onDelete: (post: PostListItem) => void;
  onMoveToProject?: (post: PostListItem) => void;
  hideProject?: boolean;
}

const GHOST_DEPTH_CLASS = [styles.ghostOne, styles.ghostTwo, styles.ghostThree];

const PostCard = React.memo(
  ({
    post,
    onOpen,
    onEditInfo,
    onLearners,
    onLearnerProgress,
    onDelete,
    onMoveToProject,
    hideProject,
  }: Props) => {
    const timeAgo = formatTimeAgo(post.dateUpdated);
    const countLabel = postsText.itemsLabel(post.itemsCount);
    const canOpenLearners = !!post.publishedCourseId && !!post.enrollNumber;
    const stacked = post.itemsCount > 1;

    const learnersBadgeContent = (
      <>
        <Icon
          name="workspace_temporal_learners.svg"
          className={styles.learnersIcon}
        />
        {post.enrollNumber || postsText.notShared}
      </>
    );

    return (
      <div
        className={`${styles.cardContainer} ${stacked ? styles.stacked : ''}`}>
        {/* The list only carries the cover of the first item, so the cards
            peeking out are left blank rather than repeating it. */}
        {stacked &&
          GHOST_DEPTH_CLASS.map((depthClass, index) => (
            <span key={index} className={`${styles.ghost} ${depthClass}`} />
          ))}
        <div className={styles.card} onClick={() => onOpen(post)}>
          <div className={styles.imageContainer}>
            {post.poster ? (
              <ImageWithFallback src={post.poster} className={styles.image} />
            ) : (
              <Icon name="posts-menu.svg" className={styles.fallbackIcon} />
            )}
            <span className={styles.countChip}>{countLabel}</span>
          </div>
          <div className={styles.titleContainer}>
            {!hideProject && post.projectName && (
              <div className={styles.projectRow}>
                <Icon name="projects-menu.svg" className={styles.projectIcon} />
                <span>{post.projectName}</span>
              </div>
            )}
            <Tooltip tooltipText={post.title}>
              <p>{post.title}</p>
            </Tooltip>
            <div className={styles.metaRow}>
              {timeAgo && <span>{postsText.updatedAgo(timeAgo)}</span>}
              {canOpenLearners ? (
                <button
                  className={`${styles.learnersBadge} ${styles.learnersBadgeButton}`}
                  title={postsText.viewLearnerProgress}
                  onClick={ev => {
                    ev.stopPropagation();
                    onLearnerProgress(post);
                  }}>
                  {learnersBadgeContent}
                </button>
              ) : (
                <div
                  className={`${styles.learnersBadge} ${
                    post.enrollNumber ? '' : styles.learnersBadgeEmpty
                  }`}>
                  {learnersBadgeContent}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={styles.optionsPosition}
          onClick={ev => ev.stopPropagation()}>
          <MicroreelCardOptions
            options={[
              {label: postsText.open, onClick: () => onOpen(post)},
              {label: postsText.editInfo, onClick: () => onEditInfo(post)},
              {
                label: postsText.learners,
                onClick: () => onLearners(post),
                disabled: !post.publishedCourseId,
              },
              ...(onMoveToProject
                ? [
                    {
                      label: postsText.moveToProject,
                      onClick: () => onMoveToProject(post),
                    },
                  ]
                : []),
              {
                label: postsText.delete,
                onClick: () => onDelete(post),
                danger: true,
              },
            ]}
          />
        </div>
      </div>
    );
  },
);

export default PostCard;
